import React, { useState, useEffect } from "react";
import {
  Row,
  Column,
  FormGroup,
  FileUploader,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from "carbon-components-react";
import { TrashCan32 } from "@carbon/icons-react";
import http from "../../http-common";
import { toast } from "react-toastify";
import { getDocumentTypeDescription } from "../../services/Utils";

const DocumentForm = ({
  setValue,
  documents,
  setDocuments,
  isUpdate = false,
  applicationUuid = null,
}) => {
  const [fileStatus, setFileStatus] = useState({
    COMPANY_EXPERIENCE: "edit",
    PROJECT_TIMELINE: "edit",
    INVESTMENT_MAP: "edit",
  });

  useEffect(() => {
    if (isUpdate && applicationUuid) {
      fetchExistingDocuments();
    }
  }, [isUpdate, applicationUuid]);

  const fetchExistingDocuments = async () => {
    try {
      const response = await http.get(
        `/applications/documents/${applicationUuid}`
      );
      const fetchedDocuments = response.data;

      // Convert fetched documents to our format
      const formattedDocuments = fetchedDocuments.map((doc) => ({
        id: doc.id,
        fileName: doc.fileName,
        storedName: doc.storedName,
        filePath: doc.filePath,
        fileType: doc.fileType,
        documentType: doc.documentType,
        size: doc.size,
        existingFile: true,
      }));

      setDocuments(formattedDocuments);

      // Update file status for existing documents
      const newFileStatus = { ...fileStatus };
      formattedDocuments.forEach((doc) => {
        newFileStatus[doc.documentType] = "complete";
      });
      setFileStatus(newFileStatus);
    } catch (error) {
      toast.error("Erro ao carregar documentos existentes");
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (documentType) => async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo excede o limite de 10MB");
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      return;
    }

    if (!file.type.includes("pdf")) {
      toast.error("Apenas arquivos PDF são permitidos");
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      return;
    }

    try {
      const base64Content = await fileToBase64(file);

      // Create new document object
      const newDocument = {
        fileName: file.name,
        fileContent: base64Content,
        fileType: file.type,
        documentType: documentType,
        size: file.size,
      };

      // Remove any existing document of the same type
      const filteredDocuments = documents.filter(
        (doc) => doc.documentType !== documentType
      );

      // Add new document
      setDocuments([...filteredDocuments, newDocument]);
      setFileStatus((prev) => ({ ...prev, [documentType]: "complete" }));

      toast.success("Arquivo carregado com sucesso");
    } catch (error) {
      toast.error("Erro ao processar arquivo");
    }
  };

  const handleRemove = async (documentType) => {
    const documentToRemove = documents.find(
      (doc) => doc.documentType === documentType
    );

    if (!documentToRemove) return;

    try {
      if (isUpdate && applicationUuid && documentToRemove.id) {
        // Remove from server if document exists
        await http.delete(`/applications/documents/${documentToRemove.id}`);
      }

      // Update local state without triggering a reload
      setDocuments((prev) =>
        prev.filter((doc) => doc.documentType !== documentType)
      );
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      toast.success("Documento removido com sucesso");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao remover documento"
      );
      // If there's an error, don't update the local state
      return;
    }
  };

  const getExistingDocument = (documentType) => {
    return documents.find((doc) => doc.documentType === documentType);
  };

  return (
    <FormGroup legendText="DOCUMENTOS DO PROJECTO" className="section-header">
      <fieldset className="bx--fieldset">
        <Row className="mb-4">
          <Column sm={12} md={12} lg={12} className="required">
            <FileUploader
              id="company-experience-uploader"
              labelTitle="10.1 Experiência do Consultor"
              labelDescription="Apresente referencias de pelo menos 3 trabalhos similares desenvolvidos pela sua empresa. (formato PDF)"
              buttonLabel="Carregar Arquivo"
              accept={[".pdf"]}
              multiple={false}
              filenameStatus={fileStatus.COMPANY_EXPERIENCE}
              onChange={handleFileChange("COMPANY_EXPERIENCE")}
              onDelete={() => handleRemove("COMPANY_EXPERIENCE")}
              value={getExistingDocument("COMPANY_EXPERIENCE")?.fileName || ""}
            />
          </Column>
        </Row>

        <Row>
          <Column sm={12} md={12} lg={12} className="required">
            <FileUploader
              id="project-timeline-uploader"
              labelTitle="10.2 Cronograma do Projecto"
              labelDescription="Apresente o cronograma detalhado. (formato PDF)"
              buttonLabel="Carregar Arquivo"
              accept={[".pdf"]}
              multiple={false}
              filenameStatus={fileStatus.PROJECT_TIMELINE}
              onChange={handleFileChange("PROJECT_TIMELINE")}
              onDelete={() => handleRemove("PROJECT_TIMELINE")}
              value={getExistingDocument("PROJECT_TIMELINE")?.fileName || ""}
            />
          </Column>
        </Row>

        <Row>
          <Column sm={12} md={12} lg={12} className="required">
            <FileUploader
              id="investment-map-uploader"
              labelTitle="10.3 Mapa de Investimento"
              labelDescription="Apresente o mapa de investimento detalhado. (formato PDF)"
              buttonLabel="Carregar Arquivo"
              accept={[".pdf"]}
              multiple={false}
              filenameStatus={fileStatus.INVESTMENT_MAP}
              onChange={handleFileChange("INVESTMENT_MAP")}
              onDelete={() => handleRemove("INVESTMENT_MAP")}
              value={getExistingDocument("INVESTMENT_MAP")?.fileName || ""}
            />
          </Column>
        </Row>

        {documents.length > 0 && (
          <DataTable
            rows={documents}
            headers={[
              { key: "fileName", header: "Nome do Documento" },
              { key: "documentType", header: "Tipo de Documento" },
              { key: "actions", header: "Ações" },
            ]}
          >
            {({ rows, headers, getHeaderProps, getTableProps }) => (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => (
                      <TableHeader {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.documentType}>
                      <TableCell>{doc.fileName}</TableCell>
                      <TableCell>
                        {getDocumentTypeDescription(doc.documentType)}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleRemove(doc.documentType)}
                          className="bx--btn bx--btn--ghost bx--btn--sm"
                        >
                          <TrashCan32 />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DataTable>
        )}
      </fieldset>
    </FormGroup>
  );
};

export default DocumentForm;
